/**
 * Pool Violations — detect named prioritization anti-patterns within an Item Pool
 * (or track) from the relationship/lineage data already loaded by the graph.
 *
 * See demo/PRIORITIZATION_ANTIPATTERNS.md for the catalog. Detection is structural:
 * a violation is a co-occurrence of items + relationships that the platform surfaces
 * BEFORE a ranking session runs. Each maps to a canon disposition (block | warn).
 */

import { getSupabaseClient } from './supabaseClient';
import { isHardSequenceLink } from './sequenceRelationships';

export type Disposition = 'block' | 'warn';

export interface PatternMeta {
  id: string;
  name: string;
  group: 'A' | 'B';
  disposition: Disposition;
}

/** Catalog (subset that is structurally detectable client-side). */
export const PATTERN_CATALOG: Record<string, PatternMeta> = {
  A1_two_tickets_one_bet:   { id: 'A1_two_tickets_one_bet',   name: 'Two Tickets, One Bet',         group: 'A', disposition: 'block' },
  // Owner ruling 2026-10-12: a package split across scopes is flagged, never
  // disallowed. A package almost always spans Tracks (a feature and the billing
  // work for it belong to different teams), so the other half is usually someone
  // else's to rank. The room is told; the split is settled where the Tracks meet.
  A2_half_a_package:        { id: 'A2_half_a_package',        name: 'Half a Package',               group: 'A', disposition: 'warn'  },
  A3_both_options_won:      { id: 'A3_both_options_won',      name: 'Both Options Won',             group: 'A', disposition: 'warn'  },
  A4_ranked_above_blocker:  { id: 'A4_ranked_above_blocker',  name: 'Ranked Above Its Own Blocker', group: 'A', disposition: 'warn'  },
  A5_whole_and_parts:       { id: 'A5_whole_and_parts',       name: 'Counting the Whole and the Parts', group: 'A', disposition: 'warn' },
  // Owner ruling 2026-09-04: comparing unlike things never stops a session
  // from happening or proceeding. The room is told and decides; the gate does
  // not decide for it. Canon 01 §8 amended to match.
  B1_altitude_salad:        { id: 'B1_altitude_salad',        name: 'Comparing Unlike Things',   group: 'B', disposition: 'warn'  },
  B2_lineage_amnesia:       { id: 'B2_lineage_amnesia',       name: 'Lineage Amnesia',              group: 'B', disposition: 'warn'  },
};

export interface PoolScopeOption {
  id: string;          // 'pool:<uuid>' | 'frame:<uuid>'
  kind: 'pool' | 'frame';
  name: string;
  itemIds: string[];   // the membership to inspect (attempted set for frames)
  cycleId?: string | null;
}

export interface PoolViolation {
  patternId: string;
  name: string;
  disposition: Disposition;
  detail: string;       // human label of what is wrong
  itemIds: string[];    // items involved (to highlight)
}

export interface PoolViolationReport {
  memberIds: string[];
  violations: PoolViolation[];
  /** itemId -> worst disposition + pattern names touching it */
  perItem: Map<string, { disposition: Disposition; patterns: string[] }>;
  blockCount: number;
  warnCount: number;
}

/** Rows arrive in camelCase or snake_case depending on the source; shapes cover both. */
interface ItemRow { id: string; title?: string | null; item_category?: string | null; itemCategory?: string | null; altitude_level?: number | null; altitudeLevel?: number | null }
interface EdgeRow { sourceItemId: string; targetItemId: string; type: string; subtype?: string | null; status?: string | null }
interface GroupRow { id: string; type: string; subtype?: string | null; status?: string | null }
interface MembershipRow { groupId: string; itemId: string }
interface LineageEventRow { eventType?: string | null; event_type?: string | null; sourceItemIds?: string[] | null; source_item_ids?: string[] | null; targetItemIds?: string[] | null; target_item_ids?: string[] | null }

interface GraphData {
  items: ItemRow[];
  edges: EdgeRow[];            // item_relationship_edges
  groups: GroupRow[];          // item_relationship_groups
  memberships: MembershipRow[]; // item_relationship_group_memberships
  lineageEvents: LineageEventRow[]; // lineage_events
}

const cat = (i: ItemRow | undefined) => i?.item_category ?? i?.itemCategory ?? null;
const alt = (i: ItemRow | undefined) => i?.altitude_level ?? i?.altitudeLevel ?? null;
const evType = (e: LineageEventRow) => e?.eventType ?? e?.event_type ?? null;
const srcIds = (e: LineageEventRow) => e?.sourceItemIds ?? e?.source_item_ids ?? [];
const tgtIds = (e: LineageEventRow) => e?.targetItemIds ?? e?.target_item_ids ?? [];

function addPerItem(map: PoolViolationReport['perItem'], itemId: string, disposition: Disposition, name: string) {
  const cur = map.get(itemId);
  if (!cur) { map.set(itemId, { disposition, patterns: [name] }); return; }
  if (!cur.patterns.includes(name)) cur.patterns.push(name);
  if (disposition === 'block') cur.disposition = 'block';
}

/** Pure structural detection over a member set. */
export function computeViolations(memberIds: string[], data: GraphData): PoolViolationReport {
  const member = new Set(memberIds);
  const byId = new Map(data.items.map((i) => [i.id, i]));
  const violations: PoolViolation[] = [];
  const perItem: PoolViolationReport['perItem'] = new Map();

  const push = (patternId: string, detail: string, itemIds: string[]) => {
    const meta = PATTERN_CATALOG[patternId];
    if (!meta) return;
    const ids = itemIds.filter((id) => member.has(id));
    if (ids.length === 0) return;
    violations.push({ patternId, name: meta.name, disposition: meta.disposition, detail, itemIds: ids });
    for (const id of ids) addPerItem(perItem, id, meta.disposition, meta.name);
  };

  // B1 — Altitude Salad: members span more than one (category × altitude) Track Cell
  const cells = new Map<string, string[]>();
  for (const id of member) {
    const it = byId.get(id); if (!it) continue;
    const key = `${cat(it) ?? '?'}|L${alt(it) ?? '?'}`;
    (cells.get(key) ?? cells.set(key, []).get(key)!).push(id);
  }
  if (cells.size > 1) {
    // minority cells are the offenders
    const sorted = [...cells.entries()].sort((a, b) => b[1].length - a[1].length);
    const offenders = sorted.slice(1).flatMap(([, ids]) => ids);
    push('B1_altitude_salad', `Members span ${cells.size} Track Cells (category × altitude) — e.g. an Opportunity/initiative alongside Solutions/features. Ranking them together is a judgement the room makes knowingly; it is not prevented.`, offenders);
  }

  // A1 — Two Tickets, One Bet: unresolved duplicate cluster with >=2 members in scope.
  //
  // `candidate` only. A cluster IS the resolution of the duplicate — once it is
  // settled (`confirmed`, written by the cluster-resolution flow for both
  // duplicate and variant; or `active`) the item list shows it as a single entry
  // under the cluster's name, so its members cannot double-count and there is
  // nothing left to block. A1 was written for the unresolved case; blocking a
  // settled cluster was compensating for the item list not representing it.
  for (const g of data.groups) {
    if (g.type !== 'cluster') continue;
    if (g.status !== 'candidate') continue;
    const gm = data.memberships.filter((m) => m.groupId === g.id).map((m) => m.itemId).filter((id) => member.has(id));
    if (gm.length >= 2) push('A1_two_tickets_one_bet', `Unresolved duplicate cluster${g.subtype ? ` (${g.subtype})` : ''}: identity must be resolved before these can compete.`, gm);
  }

  // A2 — Half a Package: package group only partially represented in scope
  for (const g of data.groups) {
    if (g.type !== 'package') continue;
    const all = data.memberships.filter((m) => m.groupId === g.id).map((m) => m.itemId);
    if (all.length < 2) continue;
    const inPool = all.filter((id) => member.has(id));
    const missing = all.filter((id) => !member.has(id));
    if (inPool.length > 0 && missing.length > 0) {
      const missingTitles = missing.map((id) => byId.get(id)?.title ?? id).join(', ');
      push('A2_half_a_package', `Part of a package — the rest is ranked elsewhere: ${missingTitles}.`, inPool);
    }
  }

  // Elaboration / composition / dependency edges
  const elaborationByParent = new Map<string, string[]>();
  for (const e of data.edges) {
    if (e.status === 'dissolved' || e.status === 'dismissed') continue;
    const s = e.sourceItemId, t = e.targetItemId;
    if (e.type === 'elaboration') {
      (elaborationByParent.get(s) ?? elaborationByParent.set(s, []).get(s)!).push(t);
    }
    // A5 — Counting the Whole and the Parts: aggregation/breakdown parent + child both in scope
    if ((e.type === 'aggregation' || e.type === 'breakdown') && member.has(s) && member.has(t)) {
      push('A5_whole_and_parts', `Composition parent and its child are both in scope — capacity/credit double-counts.`, [s, t]);
    }
    // A4 — Ranked Above Its Own Blocker: a hard (or gate) sequence edge to an item
    // outside the scope, read with one rule whichever end asserted it (canon 12 §5.4)
    if (isHardSequenceLink(e)) {
      const inScope = member.has(s) ? s : (member.has(t) ? t : null);
      const outScope = member.has(s) ? t : (member.has(t) ? s : null);
      if (inScope && outScope && !member.has(outScope)) {
        push('A4_ranked_above_blocker', `Hard dependency on an item outside this scope (${byId.get(outScope)?.title ?? outScope}) — cannot legitimately commit until addressed.`, [inScope]);
      }
    }
  }
  // A3 — Both Options Won: an elaboration parent with >=2 option children in scope
  for (const [, children] of elaborationByParent) {
    const inPool = children.filter((id) => member.has(id));
    if (inPool.length >= 2) push('A3_both_options_won', `Multiple elaboration options of the same parent are present — select one before ranking.`, inPool);
  }

  // B2 — Lineage Amnesia: a fork's source + successor both active in scope
  for (const le of data.lineageEvents) {
    if (evType(le) !== 'fork') continue;
    const both = [...srcIds(le), ...tgtIds(le)].filter((id: string) => member.has(id));
    if (both.length >= 2) push('B2_lineage_amnesia', `A forked item and its source are both in scope — one bet is double-represented.`, both);
  }

  let blockCount = 0, warnCount = 0;
  for (const v of violations) {
    if (v.disposition === 'block') blockCount++;
    else warnCount++;
  }
  return { memberIds, violations, perItem, blockCount, warnCount };
}

/** Load the selectable pools + tracks for the workspace (for the picker). */
export async function loadPoolScopeOptions(workspaceId: string): Promise<PoolScopeOption[]> {
  const supabase = getSupabaseClient();
  const options: PoolScopeOption[] = [];

  // Tracks (one per cycle) — prefer the attempted set so violations show
  interface ComparisonSetRow { id: string; name?: string | null; item_ids?: string[] | null; cycle_id?: string | null; filter_rules?: { initial_attempt?: { attempted_item_ids?: string[] } } | null }
  const { data: frames } = await supabase
    .from('cycle_comparison_sets')
    .select('id,name,item_ids,cycle_id,filter_rules')
    .eq('workspace_id', workspaceId);
  for (const f of (frames ?? []) as ComparisonSetRow[]) {
    const attempted = f.filter_rules?.initial_attempt?.attempted_item_ids;
    options.push({
      id: `frame:${f.id}`,
      kind: 'frame',
      name: f.name || 'Track',
      itemIds: attempted && attempted.length ? attempted : (f.item_ids ?? []),
      cycleId: f.cycle_id ?? null,
    });
  }

  // Item pools — canonical membership is priority_ids (same as lists UI / engine)
  interface PoolRow { id: string; name?: string | null; cycle_id?: string | null; priority_ids?: string[] | null }
  const { data: pools } = await supabase
    .from('item_pools')
    .select('id,name,cycle_id,priority_ids')
    .eq('workspace_id', workspaceId);
  for (const p of (pools ?? []) as PoolRow[]) {
    const ids = (p.priority_ids ?? []).filter(Boolean);
    options.push({
      id: `pool:${p.id}`,
      kind: 'pool',
      name: p.name || 'Item pool',
      itemIds: ids,
      cycleId: p.cycle_id ?? null,
    });
  }

  return options;
}
